import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {apiService} from '../services/api';

const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_EXPIRES_KEY = 'adminTokenExpiresAt';

interface AdminSession {
  token: string;
  expiresAt: number;
}

interface AdminContextValue {
  token: string | null;
  expiresAt: number | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

const readStoredAdminSession = (): AdminSession | null => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const expiresAtRaw = localStorage.getItem(ADMIN_EXPIRES_KEY);
  const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : 0;
  if (!token || !expiresAt || Number.isNaN(expiresAt)) return null;
  if (Date.now() > expiresAt) {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EXPIRES_KEY);
    return null;
  }
  return {token, expiresAt};
};

export const AdminProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const stored = readStoredAdminSession();
  const [token, setToken] = useState<string | null>(stored?.token ?? null);
  const [expiresAt, setExpiresAt] = useState<number | null>(stored?.expiresAt ?? null);

  const login = useCallback(async (username: string, password: string) => {
    const response = await apiService.adminLogin(username.trim(), password);
    localStorage.setItem(ADMIN_TOKEN_KEY, response.token);
    localStorage.setItem(ADMIN_EXPIRES_KEY, response.expiresAt.toString());
    setToken(response.token);
    setExpiresAt(response.expiresAt);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EXPIRES_KEY);
    setToken(null);
    setExpiresAt(null);
  }, []);

  const value = useMemo(() => ({token, expiresAt, login, logout}), [token, expiresAt, login, logout]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return ctx;
};
